import type { Locale } from "../i18n";

const copy = {
	ja: {
		title: "プロジェクト",
		description: "同じ場所の記録や小物集をまとめて、一つのリンクで見せられます。",
		create: "プロジェクトを作成",
		emptyTitle: "作品をひとまとめに",
		emptyBody: "アップロード済みのモデルを選んで、プロジェクトや小物集を作れます。",
		loading: "プロジェクトを読み込んでいます。",
		failed: "プロジェクトを読み込めませんでした。",
		retry: "もう一度読み込む",
		edit: "編集",
		editTitle: "プロジェクトを編集",
		name: "プロジェクト名",
		namePlaceholder: "例：京都の路地、小物コレクション",
		about: "説明",
		aboutPlaceholder: "撮影した場所や、集めた作品について",
		visibility: "公開範囲",
		unlisted: "限定公開（リンクを知っている人）",
		private: "非公開（自分のみ）",
		accessHint:
			"各モデルの公開範囲・パスワード・期限も適用されます。非公開や期限切れのモデルは、共有先では表示されません。",
		duration: "1作品の表示時間",
		seconds: "秒",
		durationHint: "読み込みが終わってから、次の作品へ切り替わります。",
		models: "モデルを選択",
		search: "モデル名や形式で検索",
		noModels: "まずファイルをアップロードしてください。",
		noResults: "一致するモデルがありません。",
		selected: "スライドの順番",
		selectionHint: "最大50作品。上下ボタンで表示順を変更できます。",
		noSelection: "モデルを選ぶと、ここに表示されます。",
		up: "前へ移動",
		down: "後へ移動",
		remove: "プロジェクトから外す",
		untitled: "タイトルなし",
		save: "保存",
		saving: "保存中…",
		saveFailed: "保存できませんでした。入力は残っています。もう一度お試しください。",
		conflict: "別の画面でプロジェクトが更新されました。いったん閉じて、編集し直してください。",
		saved: "プロジェクトを保存しました。",
		cancel: "キャンセル",
		close: "閉じる",
		copy: "リンクをコピー",
		copied: "リンクをコピーしました。",
		copyFailed: "コピーできませんでした。スライドショーを開いて、アドレスをコピーしてください。",
		open: "スライドショーを開く",
		delete: "削除",
		deleting: "削除中…",
		deleted: "プロジェクトを削除しました。",
		deleteTitle: "プロジェクトを削除しますか？",
		deleteBody: "このプロジェクトの共有リンクは開けなくなります。中のモデルはライブラリに残ります。",
		deleteFailed: "削除できませんでした。最新の一覧を読み込み直してお試しください。",
		items: (count: number) => `${count}作品`,
		viewerTitle: "プロジェクト - GSplat Share",
		viewerDescription: "3Dモデルをプロジェクトにまとめて、スライドショーで楽しめます。",
		viewerFailed: "プロジェクトを開けませんでした。非公開・削除済み、または接続の問題が考えられます。",
		viewerEmpty: "表示できるモデルがありません",
		viewerEmptyHint: "モデルの公開範囲や期限を確認してください。",
		back: "ライブラリへ",
		home: "ホームへ",
		previous: "前の作品",
		next: "次の作品",
		play: "自動再生",
		pause: "一時停止",
		fullscreen: "全画面表示",
		exitFullscreen: "全画面を終了",
		fullscreenFailed: "全画面表示を開始できませんでした。",
		viewModel: "作品を個別に開く",
		protected: "パスワード付きの作品",
		modelFailed: "作品を読み込めませんでした。次の作品へ進むか、もう一度お試しください。",
		modelLoading: "作品を読み込み中…",
		interval: "切替間隔",
		slide: (current: number, total: number) => `${current} / ${total}`,
		keyboard: "← →で切替・Spaceで再生／停止",
		discardTitle: "変更を破棄しますか？",
		discardBody: "編集中のプロジェクトは保存されていません。",
		discard: "変更を破棄",
		keepEditing: "編集を続ける",
	},
	en: {
		title: "Projects",
		description: "Gather captures of a place or a collection of objects and share them with one link.",
		create: "Create project",
		emptyTitle: "Bring your captures together",
		emptyBody: "Choose uploaded models to build a project or a collection.",
		loading: "Loading projects.",
		failed: "Could not load projects.",
		retry: "Try again",
		edit: "Edit",
		editTitle: "Edit project",
		name: "Project name",
		namePlaceholder: "e.g. Kyoto alleys, Object collection",
		about: "Description",
		aboutPlaceholder: "Tell people about the place or collection",
		visibility: "Visibility",
		unlisted: "Unlisted (anyone with the link)",
		private: "Private (only you)",
		accessHint:
			"Each model keeps its visibility, password and expiration. Private and expired models are hidden from visitors.",
		duration: "Time per model",
		seconds: "seconds",
		durationHint: "The next model appears after loading finishes.",
		models: "Choose models",
		search: "Search by model name or format",
		noModels: "Upload a file to get started.",
		noResults: "No matching models.",
		selected: "Slide order",
		selectionHint: "Up to 50 models. Use the arrows to change the order.",
		noSelection: "Your selected models will appear here.",
		up: "Move earlier",
		down: "Move later",
		remove: "Remove from project",
		untitled: "Untitled",
		save: "Save",
		saving: "Saving…",
		saveFailed: "Could not save. Your edits are still here. Please try again.",
		conflict: "This project changed in another window. Close this editor and reopen it before saving.",
		saved: "Project saved.",
		cancel: "Cancel",
		close: "Close",
		copy: "Copy link",
		copied: "Link copied.",
		copyFailed: "Could not copy. Open the slideshow and copy its address.",
		open: "Open slideshow",
		delete: "Delete",
		deleting: "Deleting…",
		deleted: "Project deleted.",
		deleteTitle: "Delete project?",
		deleteBody: "The project link will stop working. Its models will remain in your library.",
		deleteFailed: "Could not delete. Reload the project list and try again.",
		items: (count: number) => `${count} models`,
		viewerTitle: "Project - GSplat Share",
		viewerDescription: "Explore a collection of 3D models in a project slideshow.",
		viewerFailed: "Could not open this project. It may be private or deleted, or the connection may be unavailable.",
		viewerEmpty: "No models available",
		viewerEmptyHint: "Check the models’ visibility and expiration.",
		back: "Back to library",
		home: "Home",
		previous: "Previous model",
		next: "Next model",
		play: "Autoplay",
		pause: "Pause",
		fullscreen: "Fullscreen",
		exitFullscreen: "Exit fullscreen",
		fullscreenFailed: "Could not enter fullscreen.",
		viewModel: "Open model separately",
		protected: "Password-protected model",
		modelFailed: "Could not load this model. Try again or move to the next model.",
		modelLoading: "Loading model…",
		interval: "Slide interval",
		slide: (current: number, total: number) => `${current} / ${total}`,
		keyboard: "← → to navigate · Space to play or pause",
		discardTitle: "Discard changes?",
		discardBody: "Your project edits have not been saved.",
		discard: "Discard changes",
		keepEditing: "Keep editing",
	},
};

export function getProjectCopy(locale: Locale) {
	return copy[locale];
}
